import type { SegmentSheetData } from "@/shared/store/segmentSheetStore"

function columnValueToDisplayString(
  columnKey: string | undefined,
  raw: unknown
): string | null {
  if (typeof raw === "string") return raw
  if (typeof raw === "boolean") return raw ? "true" : "false"
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (
      columnKey === "winrate" &&
      raw >= 0 &&
      raw <= 1
    ) {
      const pct = raw * 100
      const s = pct.toFixed(1)
      return s.endsWith(".0") ? `${Math.round(pct)}%` : `${s}%`
    }
    if (Number.isInteger(raw)) return String(raw)
    return raw.toLocaleString(undefined, { maximumFractionDigits: 8 })
  }
  return null
}

/** Parse `segment_popup` / emulator JSON into `SegmentSheetData` for `useSegmentSheetStore.show`. */
export function parseSegmentSheetPayload(
  parsed: unknown
): { data: SegmentSheetData; error: null } | { data: null; error: string } {
  if (!parsed || typeof parsed !== "object") {
    return { data: null, error: "Root value must be a JSON object" }
  }
  const o = parsed as Record<string, unknown>

  const title = o.title
  if (typeof title !== "string") {
    return { data: null, error: "`title` (string) is required" }
  }
  const segment = o.segment
  if (typeof segment !== "string") {
    return { data: null, error: "`segment` (string) is required" }
  }
  const campaignIdRaw = o.campaign_id ?? o.campaignId
  const campaignId =
    typeof campaignIdRaw === "string" && campaignIdRaw.trim().length > 0
      ? campaignIdRaw.trim()
      : undefined
  const button_text = o.button_text
  if (typeof button_text !== "string") {
    return { data: null, error: "`button_text` (string) is required" }
  }
  const button_url = o.button_url
  if (typeof button_url !== "string") {
    return { data: null, error: "`button_url` (string) is required" }
  }

  if (!Array.isArray(o.columns)) {
    return { data: null, error: "`columns` must be an array" }
  }
  const columns: SegmentSheetData["columns"] = []
  for (let i = 0; i < o.columns.length; i++) {
    const col = o.columns[i]
    if (!col || typeof col !== "object") {
      return { data: null, error: `columns[${i}] must be an object` }
    }
    const c = col as Record<string, unknown>
    const name = c.name
    if (typeof name !== "string") {
      return { data: null, error: `columns[${i}] needs string "name"` }
    }
    const columnKey = c.key
    const keyStr = typeof columnKey === "string" ? columnKey : undefined
    const valueStr = columnValueToDisplayString(keyStr, c.value)
    if (valueStr === null) {
      return {
        data: null,
        error: `columns[${i}] "value" must be a string, number, or boolean`,
      }
    }
    const entry: SegmentSheetData["columns"][number] = {
      name,
      value: valueStr,
    }
    if (keyStr) entry.key = keyStr
    columns.push(entry)
  }

  const emptyWidget: SegmentSheetData["widget"] = {
    title: "",
    content: "",
    special_text: "",
  }
  let widget = emptyWidget
  const w = o.widget
  if (w != null) {
    if (typeof w !== "object") {
      return { data: null, error: "`widget` must be an object" }
    }
    const wo = w as Record<string, unknown>
    const wt = wo.title
    const wc = wo.content
    const ws = wo.special_text
    widget = {
      title: typeof wt === "string" ? wt : "",
      content: typeof wc === "string" ? wc : "",
      special_text: typeof ws === "string" ? ws : "",
    }
  }

  return {
    data: {
      title,
      segment,
      ...(campaignId ? { campaignId } : {}),
      columns,
      widget,
      button_text,
      button_url,
    },
    error: null,
  }
}
